import React from "react";
import { Download, Loader2, Volume2, Play, X } from "lucide-react";
import { Button } from "../ui/button";
import { downloadOrShareFile } from "../../utils/pdfShare";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import CallRecordingPlayer from "../audio/CallRecordingPlayer";

/**
 * InlineRecordingPlayer
 * ─────────────────────
 * Compact recording control rendered inline on a call row in the Call History
 * list. Uses the shared <CallRecordingPlayer> (seek bar, ±10s skip, speed,
 * time readout) so playback behaves identically across the platform.
 * Renders nothing if the call has no `recording_url`.
 *
 * Playback exclusivity: the PARENT owns a single `playingId` — only the
 * active row mounts the player, so switching rows stops the previous one.
 */
const InlineRecordingPlayer = ({
  call,
  getRecordingProxyUrl,
  formatDuration,
  isPlaying,
  onTogglePlay,
  audioRefSetter, // kept for prop compatibility (unmount now stops audio)
  onEnded,
}) => {
  const [downloading, setDownloading] = React.useState(false);

  if (!call?.recording_url) return null;

  const audioUrl = getRecordingProxyUrl(call);
  const callId = call.call_id || call.call_sid || call.twilio_sid;
  // Prefer the precise recording duration captured by Twilio; fall back to
  // the call duration so we always show *something* meaningful.
  const seconds = call.recording_duration || call.duration || 0;

  const buildFileName = () => {
    const callerNumber = call.direction === "inbound" ? call.from_number : call.to_number;
    const dateStr = call.created_at
      ? format(parseISO(call.created_at), "yyyy-MM-dd")
      : "recording";
    return `Call_Recording_${(call.customer_name || callerNumber || "call")
      .replace(/[^a-z0-9]+/gi, "_")}_${dateStr}.mp3`;
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onTogglePlay?.();
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!audioUrl) {
      toast.error("No recording URL available");
      return;
    }
    setDownloading(true);
    const token = localStorage.getItem("token");
    try {
      await downloadOrShareFile(audioUrl, buildFileName(), "audio/mpeg", token);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Download failed");
        window.open(audioUrl, "_blank");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="px-4 pb-3 -mt-1"
      onClick={(e) => e.stopPropagation()}
      data-testid={`inline-recording-${callId}`}
    >
      <div className="flex items-center gap-2 rounded-lg border border-blue-500/20 bg-blue-500/[0.04] px-2.5 py-1.5">
        <Button
          type="button"
          size="icon"
          onClick={handleClick}
          className={`h-8 w-8 rounded-full shrink-0 ${
            isPlaying
              ? "bg-muted hover:bg-muted/80 text-foreground"
              : "bg-blue-500/15 hover:bg-blue-500/25 text-blue-600 dark:text-blue-300"
          }`}
          data-testid={`inline-recording-play-btn-${callId}`}
          aria-label={isPlaying ? "Close recording player" : "Play recording"}
        >
          {isPlaying ? <X className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
        </Button>

        {isPlaying && audioUrl ? (
          <CallRecordingPlayer
            compact
            autoPlay
            src={audioUrl}
            knownDuration={seconds}
            fileName={buildFileName()}
            showDownload={false}
            onEnded={() => onEnded?.()}
            testId={`inline-recording-player-${callId}`}
          />
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <Volume2 className="w-3 h-3 text-blue-500 shrink-0" />
            <span className="font-medium text-foreground/80">Recording</span>
            {seconds > 0 && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="font-mono">{formatDuration(seconds)}</span>
              </>
            )}
          </div>
        )}

        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={handleDownload}
          disabled={downloading}
          className="h-7 w-7 ml-auto text-muted-foreground hover:text-foreground shrink-0"
          data-testid={`inline-recording-download-btn-${callId}`}
          aria-label="Download recording"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
};

export default InlineRecordingPlayer;
